import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../services/ApiService';
import companionService, { CompanionAgentStatus, CompanionHealth } from '../services/CompanionService';

/**
 * Stato del Desktop Agent sul PC dell'amministratore, condiviso da tutto l'admin
 * (stesso schema del portale: DesktopAgentContext). Lo legge l'icona nella barra in
 * alto e ogni pagina che fa lavorare l'agent.
 *
 * - offline: l'agent non risponde su 127.0.0.1 (non installato o non avviato);
 * - limited: risponde, ma non ha una CLI con profilo di sola lettura;
 * - ready: pronto a eseguire in sola lettura.
 */
export type DesktopAgentState = 'checking' | 'offline' | 'limited' | 'ready';

interface DesktopAgentValue {
    state: DesktopAgentState;
    health: CompanionHealth | null;
    port: number;
    /** CLI installate con profilo di sola lettura, quelle che lo studio può usare. */
    readOnlyAgents: CompanionAgentStatus[];
    /** CLI scelta per lavorare; la prima disponibile se non è stata scelta. */
    agent: string | null;
    setAgent: (code: string) => void;
    /** Cartella del repository di Semplora sul PC, ricordata in questo browser. */
    workspace: string;
    setWorkspace: (path: string) => void;
    refresh: () => Promise<void>;
}

const Context = createContext<DesktopAgentValue | null>(null);

const WORKSPACE_KEY = 'easytools.desktopAgent.workspace';
const LEGACY_WORKSPACE_KEY = 'easytools.issueAnalyst.workspace';
const AGENT_KEY = 'easytools.desktopAgent.agent';

const read = (key: string) => { try { return localStorage.getItem(key); } catch { return null; } };
const write = (key: string, value: string) => { try { localStorage.setItem(key, value); } catch { /* storage non disponibile */ } };

export const DesktopAgentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [health, setHealth] = useState<CompanionHealth | null>(null);
    const [port, setPort] = useState(4977);
    const [checking, setChecking] = useState(true);
    const [workspace, setWorkspaceState] = useState(() => read(WORKSPACE_KEY) ?? read(LEGACY_WORKSPACE_KEY) ?? '');
    const [chosenAgent, setChosenAgent] = useState(() => read(AGENT_KEY));
    const inFlight = useRef<Promise<void> | null>(null);

    const refresh = useCallback(() => {
        if (inFlight.current) return inFlight.current;
        setChecking(true);
        const request = (async () => {
            const info = await apiService.getCompanionInfo<{ healthPort?: number }>().catch(() => null);
            const nextPort = info?.healthPort ?? 4977;
            const nextHealth = await companionService.health(nextPort).catch(() => null);
            setPort(nextPort);
            setHealth(nextHealth);
            setChecking(false);
        })();
        inFlight.current = request;
        void request.finally(() => { inFlight.current = null; });
        return request;
    }, []);

    // Controllo all'avvio, ogni 30 secondi con la pagina visibile e quando si torna sulla finestra.
    useEffect(() => {
        void refresh();
        const onVisible = () => { if (!document.hidden) void refresh(); };
        const timer = window.setInterval(onVisible, 30_000);
        window.addEventListener('focus', onVisible);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener('focus', onVisible);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [refresh]);

    const value = useMemo<DesktopAgentValue>(() => {
        const readOnlyAgents = (health?.agents ?? []).filter(a => a.available && a.supportsReadOnly);
        const state: DesktopAgentState = checking && !health ? 'checking' : !health ? 'offline' : readOnlyAgents.length ? 'ready' : 'limited';
        const agent = readOnlyAgents.find(a => a.code === chosenAgent)?.code ?? readOnlyAgents[0]?.code ?? null;
        return {
            state, health, port, readOnlyAgents, agent,
            setAgent: code => { setChosenAgent(code); write(AGENT_KEY, code); },
            workspace,
            setWorkspace: path => { setWorkspaceState(path); write(WORKSPACE_KEY, path); },
            refresh,
        };
    }, [health, checking, port, chosenAgent, workspace, refresh]);

    return <Context.Provider value={value}>{children}</Context.Provider>;
};

export function useDesktopAgent(): DesktopAgentValue {
    const value = useContext(Context);
    if (!value) throw new Error('DesktopAgentProvider is required');
    return value;
}

export const DESKTOP_AGENT_STATE_LABEL: Record<DesktopAgentState, string> = {
    checking: 'Verifica in corso',
    offline: 'Non raggiungibile',
    limited: 'Collegato, senza CLI in sola lettura',
    ready: 'Pronto',
};
